import type { StateCreator } from "zustand/vanilla";

import type { SwitchOverrides } from "../../embed/bridgeMessages";

/**
 * EmbedContent is the live content the embedded studio renders when its host drives it
 * at runtime through the `mountaya:setItinerary` postMessage command: the active
 * itinerary id plus the accumulated display overrides the host has sent. It is set only
 * by an inbound command (null until the host posts one), so a public embed the host
 * never drives — and the first-party share surface, which has no host — keeps rendering
 * the route's resolved config unchanged. The overrides ACCUMULATE across commands (the
 * setContent tool merges each message onto the previous slot) so an omitted field leaves
 * the current value in place, matching the documented postMessage contract.
 */
export interface EmbedContent {
  itineraryId: string;
  overrides: SwitchOverrides;
}

/**
 * EmbedContentSliceState is the live embed-content slot the host drives through
 * postMessage: null until the first `setItinerary` command lands.
 */
export interface EmbedContentSliceState {
  embedContent: EmbedContent | null;
}

/**
 * EmbedContentSliceActions is the whole-slot setter the embed setContent tool drives,
 * reached only through the tool bus.
 */
export interface EmbedContentSliceActions {
  setEmbedContent: (embedContent: EmbedContent | null) => void;
}

/**
 * embedContentInitialState seeds the live embed-content slot empty, so the surface
 * renders the route's resolved config until the host posts its first command.
 */
export const embedContentInitialState: EmbedContentSliceState = {
  embedContent: null,
};

/**
 * createEmbedContentSlice builds the live embed-content action. The single whole-slot
 * setter records the host's latest content command (the itinerary + accumulated
 * overrides) or clears it; the setContent tool computes the accumulation before calling
 * it, so this slice stays a minimal setter like the other open/close slices.
 */
export const createEmbedContentSlice: StateCreator<
  EmbedContentSliceState & EmbedContentSliceActions,
  [],
  [],
  EmbedContentSliceActions
> = (set) => ({
  setEmbedContent: (embedContent) => set({ embedContent }),
});

/**
 * selectEmbedContent is the live embed-content slot, or null when the host has not
 * driven the embed. The surface reads it to fold the host's overrides over the route's
 * resolved config; narrow so the surface re-renders only when the content command
 * changes.
 */
export const selectEmbedContent = (state: EmbedContentSliceState): EmbedContent | null =>
  state.embedContent;
